import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/api/client'

export interface WidgetConfig {
  id: string
  enabled: boolean
  order: number
}

interface DashboardConfigResponse {
  widgets: WidgetConfig[]
  layoutMode?: string
  istituzione?: boolean
}

export type LayoutMode = 'sidebar' | 'topbar'

const WIDGETS_KEY = 'immigreer_dashboard_widgets'
const LAYOUT_KEY = 'immigreer_layout_mode'
const DARK_KEY = 'immigreer_dark_mode'

// Ordine di default richiesto (per chi NON ha personalizzato la dashboard):
// kpi, da fare, appuntamenti, messaggi, timbrature, personale, assenze, scadenze, calendario,
// centri, manutenzioni, mezzi, whatsapp, richieste (ticket), accesso rapido.
const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: 'kpi-strip', enabled: true, order: 0 },
  // Subito sotto i KPI: è la prima cosa che serve sapere aprendo il portale ("cosa devo fare").
  // Non serve una migration per accenderlo a chi ha già una config salvata: mergeWithDefaults
  // parte da DEFAULT_WIDGETS, quindi un id nuovo entra con il suo default (enabled).
  { id: 'da-fare', enabled: true, order: 1 },
  { id: 'prossimi-appuntamenti', enabled: true, order: 2 },
  { id: 'messaggi', enabled: true, order: 3 },
  { id: 'timbrature', enabled: true, order: 4 },
  { id: 'statistiche-personale', enabled: true, order: 5 },
  { id: 'statistiche-assenze', enabled: true, order: 6 },
  { id: 'scadenze-documenti', enabled: true, order: 7 },
  { id: 'calendario-settimana', enabled: false, order: 8 },
  { id: 'centri-grid', enabled: true, order: 9 },
  { id: 'statistiche-manutenzioni', enabled: true, order: 10 },
  { id: 'statistiche-mezzi', enabled: true, order: 11 },
  // Attivo di default: il widget è visibile a tutti (stato WA in sola lettura, nessun permesso
  // richiesto); chi non lo vuole lo disattiva e la scelta resta salvata. V235 accende una tantum
  // le config salvate che lo avevano a false implicito (salvataggi precedenti al cambio default).
  { id: 'whatsapp', enabled: true, order: 12 },
  { id: 'ticket', enabled: true, order: 13 },
  // Assistente AI: pannello di attivazione + token residui. Visibile SOLO agli amministratori
  // dell'azienda (gate in DashboardView.widgetVisible): è lì che si accetta l'informativa.
  { id: 'assistente-ai', enabled: true, order: 16 },
  { id: 'accesso-rapido', enabled: false, order: 14 },
  // Visibile solo a chi ha MAIL_READ (gate applicato da widgetVisible in DashboardView).
  { id: 'webmail', enabled: true, order: 15 },
  // Avanzamento del porting verso il portale nuovo: visibile a tutti (comunicazione).
  // order alto = SEMPRE in fondo di default, anche per chi ha riordinato gli altri widget.
  { id: 'porting', enabled: true, order: 99 },
]

export const useDashboardStore = defineStore('dashboard', () => {
  const widgets = ref<WidgetConfig[]>([...DEFAULT_WIDGETS])
  const loaded = ref(false)
  const istituzione = ref(false)

  // Layout e dark mode letti subito da localStorage per evitare flash al reload
  const layoutMode = ref<LayoutMode>(
    (localStorage.getItem(LAYOUT_KEY) as LayoutMode | null) ?? 'sidebar'
  )
  const isDark = ref<boolean>(localStorage.getItem(DARK_KEY) === 'true')

  const orderedActiveWidgets = computed(() =>
    [...widgets.value]
      .filter(w => w.enabled)
      .sort((a, b) => a.order - b.order)
  )

  const allWidgets = computed(() =>
    [...widgets.value].sort((a, b) => a.order - b.order)
  )

  // ── Config API ────────────────────────────────────────────────────

  async function loadConfig() {
    try {
      const res = await apiClient.get<DashboardConfigResponse>(
        '/api/v2/dashboard/config'
      )
      istituzione.value = !!res.data?.istituzione
      if (res.status === 204 || !res.data?.widgets) {
        loadFromLocalStorage()
      } else {
        widgets.value = mergeWithDefaults(res.data.widgets)
        if (res.data.layoutMode === 'sidebar' || res.data.layoutMode === 'topbar') {
          layoutMode.value = res.data.layoutMode
          localStorage.setItem(LAYOUT_KEY, layoutMode.value)
        }
      }
    } catch {
      loadFromLocalStorage()
    } finally {
      loaded.value = true
    }
  }

  function loadFromLocalStorage() {
    try {
      const raw = localStorage.getItem(WIDGETS_KEY)
      if (raw) widgets.value = mergeWithDefaults(JSON.parse(raw) as WidgetConfig[])
    } catch {
      // usa i default già impostati
    }
  }

  function mergeWithDefaults(saved: WidgetConfig[]): WidgetConfig[] {
    const savedMap = new Map(saved.map(w => [w.id, w]))
    return DEFAULT_WIDGETS.map(def => savedMap.get(def.id) ?? def)
  }

  async function saveConfig() {
    const payload = { widgets: widgets.value, layoutMode: layoutMode.value }
    localStorage.setItem(WIDGETS_KEY, JSON.stringify(widgets.value))
    try {
      await apiClient.put('/api/v2/dashboard/config', payload)
    } catch {
      // fallback su localStorage già salvato
    }
  }

  // ── Layout mode ───────────────────────────────────────────────────

  function setLayout(mode: LayoutMode) {
    layoutMode.value = mode
    localStorage.setItem(LAYOUT_KEY, mode)
    saveConfig()
  }

  // ── Dark mode ─────────────────────────────────────────────────────

  function toggleDark() {
    isDark.value = !isDark.value
    localStorage.setItem(DARK_KEY, String(isDark.value))
  }

  // ── Widget ops ────────────────────────────────────────────────────

  function toggleWidget(id: string) {
    const w = widgets.value.find(w => w.id === id)
    if (w) {
      w.enabled = !w.enabled
      reorderAfterToggle()
      saveConfig()
    }
  }

  function reorderAfterToggle() {
    const sorted = [...widgets.value].sort((a, b) => a.order - b.order)
    sorted.forEach((w, i) => { w.order = i })
    widgets.value = sorted
  }

  function updateOrder(newList: WidgetConfig[]) {
    newList.forEach((w, i) => { w.order = i })
    widgets.value = newList
    saveConfig()
  }

  function resetToDefaults() {
    widgets.value = [...DEFAULT_WIDGETS]
    saveConfig()
  }

  return {
    widgets,
    loaded,
    istituzione,
    layoutMode,
    isDark,
    orderedActiveWidgets,
    allWidgets,
    loadConfig,
    setLayout,
    toggleDark,
    toggleWidget,
    updateOrder,
    resetToDefaults,
    saveConfig,
  }
})
